/**
 * HTML Processing Utilities
 * Convert SAP relative links to absolute URLs
 */

const SAP_HELP_BASE_URL = "https://help.sap.com/docs/PRODUCTS";

/**
 * Process HTML description to convert relative SAP links to absolute URLs
 * Format: https://help.sap.com/docs/PRODUCTS/{outputloio}/{linkId}.html?version=latest&locale=en-US
 *
 * Works for both What's New and Service Catalog descriptions
 *
 * @param {string} htmlContent - Raw HTML content from SAP
 * @param {string} outputloio - SAP document identifier (outputloio/loio)
 * @returns {string} Processed HTML with absolute URLs
 */
export function processWhatsNewHtml(htmlContent, outputloio) {
  if (!htmlContent) return htmlContent;

  if (!outputloio) {
    console.warn("No outputloio provided for HTML processing");
    return htmlContent;
  }

  // Replace relative .html links with absolute SAP help URLs
  // Pattern: href="959ff8d8355c426fbeac036b1973d647.html"
  // Becomes: href="https://help.sap.com/docs/PRODUCTS/aa23dac9b58b48c6abda61fc704fb180/959ff8d8355c426fbeac036b1973d647.html?version=latest&locale=en-US"
  const processedHtml = htmlContent.replace(
    /href="([a-f0-9]{32})\.html"/gi,
    `href="${SAP_HELP_BASE_URL}/${outputloio}/$1.html?version=latest&locale=en-US" target="_blank" rel="noopener noreferrer"`,
  );

  return processedHtml;
}

/**
 * Process SAP HTML content (alias for processWhatsNewHtml for clarity)
 * Use this for Service Catalog descriptions
 *
 * @param {string} htmlContent - Raw HTML content from SAP
 * @param {string} loio - SAP document identifier (loio/outputloio)
 * @returns {string} Processed HTML with absolute URLs
 */
export function processSapHtml(htmlContent, loio) {
  return processWhatsNewHtml(htmlContent, loio);
}

/**
 * Generate SAP Help documentation URL from loio
 * @param {string} loio - SAP document identifier (outputloio)
 * @returns {string|null} Full documentation URL or null
 */
export function generateSapHelpUrl(loio) {
  if (!loio) return null;
  return `${SAP_HELP_BASE_URL}/${loio}?version=latest&locale=en-US`;
}

/**
 * Generate SAP Release Notes URL for a specific release version
 * @param {string} releaseVersion - Release version (e.g., "2602")
 * @returns {string} URL to SAP release notes page
 */
export function generateReleaseNotesUrl(releaseVersion) {
  // SAP Digital Manufacturing What's New page
  // Format: https://help.sap.com/whats-new/sap-digital-manufacturing
  return "https://help.sap.com/whats-new/sap-digital-manufacturing";
}

export default {
  processWhatsNewHtml,
  processSapHtml,
  generateSapHelpUrl,
  generateReleaseNotesUrl,
};
